<template>
  <div class="shared-link-restore-folder">
    <div class="shared-link-restore-folder-subheader">

      <el-row>
        <el-col
          :span="14"
        >

          <el-breadcrumb
            :max-items="4"
            class="my-16 ml-16">
            <el-breadcrumb-item
              class="el-text el-text--display-medium"
              v-for="item in breadcrumbItems"
              :key="item.path"
              @click.native="handleBreadcrumbItemClick(item)">
              {{ item.crumbTitle }}
            </el-breadcrumb-item>
          </el-breadcrumb>

        </el-col>
        <el-col
          :span="10"
          style="text-align: right;">

          <el-button
            type="text"
            class="mr-16"
            @click="restoreAll">
            <el-icon
              name="i-download--16"
            />
            {{ $t('shared-link.links-table.action.restore-all') }}
          </el-button>

        </el-col>
      </el-row>

    </div>
    <div
      class="shared-link-restore-folder-table">
      <el-table
        style="height: 100%;"
        height="fit-scrollable"
        :data="getLinkContentItems"
        highlight-current-row
        :default-sort = "{ prop: 'displayName', order: 'ascending'}"
        @row-click="onTableRowClick"
        @row-dblclick="onTableRowDblClick"
        @select="handleSelect"
        @select-all="handleSelect"
        @sort-change="handleSortChange"
        :loading-append="isTableDataLoading"
        ref="table"
        :empty-text="$t('shared-link.links-table.message.empty-folder')"
        border-bottom>

        <el-table-column
          type="selection"
        />

        <el-table-column
          prop="id"
          label="id"
          hidden/>

        <el-table-column
          prop="folderItem"
          label="folderItem"
          hidden/>

        <el-table-column
          prop="displayName"
          :label="$t('shared-link.links-table.column.name')"
          :sort-method="() => 0"
          align="left"
          sortable>
          <template slot-scope="{ row }">
            <el-icon :name="row.name | fileIcon(row.folderItem ? 'folder' : '')"/>
            <span class="ml-16">
              {{ row.displayName }}
            </span>
          </template>
        </el-table-column>

        <el-table-column
          prop="size"
          align="right"
          width="120"
          :label="$t('shared-link.links-table.column.size')"
          :sort-method="() => 0"
          sortable>
          <template slot-scope="{ row }">
            <span v-if="!row.folderItem">
              {{ row.size | size }}
            </span>
          </template>
        </el-table-column>

        <el-table-column
          prop="restore"
          width="100"
          align="right">
          <template slot-scope="{ row }">
            <el-icon
              color="brand-primary"
              name="i-download--16"
            />

          </template>
        </el-table-column>

      </el-table>
    </div>


    <el-details-layout
      class="el-sidebar"
      v-if="isSideBarOpen"
      open
      @close="sidebarClose()">

      <template #details>
        <el-details-panel
          @command="handleSidebarCommand"
          :title="sidebarTitle"
          show-close
          :actions="sidebarActions"
          :properties="sidebarProps">
          <template
            #preview
            v-if="isSideBarPreviewEnabled">
            <div class="el-sidebar-preview">
              <el-icon
                class="el-sidebar-icon"
                :name="sidebarPreviewIconName"/>
            </div>
          </template>
        </el-details-panel>
      </template>
    </el-details-layout>

  </div>
</template>

<style lang="scss" scoped>
  .shared-link-restore-folder-subheader {
    height: 64px;
    line-height: 64px;
    text-align: center;
  }

  .shared-link-restore-folder-table {
    height: 100%;
  }
</style>

<script>
import { createNamespacedHelpers } from 'vuex';

import {
  FETCH_LINK_CONTENT_ITEMS,
  SORT_LINK_CONTENT_ITEMS
} from '../store/action-types';

import {
  PENDING_STATUS
} from '../store/action-status';

const { mapGetters, mapActions } = createNamespacedHelpers('shared-link');

export default {
  name: 'SharedLinkRestoreFolder',
  components: {},
  data: function () {
    return {
      selectedItems: [],
      breadcrumbItems: [this.getBreadcrumbRoot()],
      rootFolderName: ''
    };
  },
  watch: {
    getFetchLinkContentItemsStatus(status) {
      if (status === PENDING_STATUS) {
        this.sidebarClose();
      } else if (this.rootFolderName === '' && this.getRootFolderName !== '') {
        this.rootFolderName = this.getRootFolderName;
        this.breadcrumbRebuild();
      }
    }
  },
  computed: {
    ...mapGetters([
      'getLinkCipher',
      'getLinkPassword',
      'getLinkContentItems',
      'getFetchLinkContentItemsStatus',
      'getRootFolderName'
    ]),
    isTableDataLoading() {
      return this.getFetchLinkContentItemsStatus === PENDING_STATUS;
    },
    sidebarTitle() {
      if (this.selectedItems.length === 1) {
        const row = this.selectedItems[0];
        return row.displayName;
      }
      return this.$tc(
        'shared-link.side-bar.selected.title:plural',
        this.selectedItems.length, { count: this.selectedItems.length }
      );
    },
    isSideBarOpen() {
      return this.selectedItems.length > 0;
    },
    isSideBarPreviewEnabled() {
      return this.selectedItems.length === 1;
    },
    sidebarPreviewIconName() {
      if (this.selectedItems.length === 1) {
        const row = this.selectedItems[0];
        return this.$options.filters.fileIcon(row.name, row.folderItem ? 'folder' : '');
      }
      return '';
    },
    sidebarProps() {
      const row = this.selectedItems[0];
      if (!row.folderItem) {
        return [
          {
            label: this.$t('shared-link.links-table.label.size'),
            value: this.$options.filters.size(row.size)
          }
        ];
      }
      return [];
    },
    sidebarActions() {
      const row = this.selectedItems[0];
      return this.getMenuActions(row);
    }
  },
  methods: {
    ...mapActions({
      fetchLinkContentItems: FETCH_LINK_CONTENT_ITEMS,
      sortLinkContentItems: SORT_LINK_CONTENT_ITEMS
    }),
    handleBreadcrumbItemClick(selectedItem) {
      this.loadContent(selectedItem);
    },
    getBreadcrumbRoot() {
      return {
        crumbTitle: this.rootFolderName,
        path: ''
      };
    },
    updateBreadcrumb(selectedItem) {
      if (selectedItem.path === '') {
        this.breadcrumbItems = [];
        this.breadcrumbItems.push(this.getBreadcrumbRoot());
      } else {
        const crumbIdx = this.breadcrumbItems.indexOf(selectedItem);
        if (crumbIdx === -1) {
          this.breadcrumbItems.push(selectedItem);
        } else {
          this.breadcrumbItems = this.breadcrumbItems.slice(0, crumbIdx + 1);
        }
      }
    },
    breadcrumbRebuild() {
      if (this.breadcrumbItems.length === 1) {
        this.breadcrumbItems = [];
        this.breadcrumbItems.push(this.getBreadcrumbRoot());
      }
    },
    loadContent(selectedItem) {
      this.fetchLinkContentItems({
        linkCipher: this.getLinkCipher,
        password: this.getLinkPassword,
        path: selectedItem.path
      });
      this.updateBreadcrumb(selectedItem);
    },
    onTableRowClick(row, event, column) {
      if (column.prop === 'restore') {
        this.restoreRow(row);
      } else if (column.prop === 'displayName' && row.folderItem) {
        this.openFolder(row);
      } else {
        this.$refs.table.clearSelection();
        this.selectedItems = [row];
      }
    },
    onTableRowDblClick(row) {
      this.openFolder(row);
    },
    openFolder(row) {
      if (row.folderItem) {
        this.sidebarClose();
        this.loadContent({
          path: row.id,
          crumbTitle: row.displayName
        });
      }
    },
    handleSidebarCommand(command) {
      if (this.selectedItems.length === 1) {
        this.restoreRow(this.selectedItems[0]);
      } else {
        this.restoreSelected(this.selectedItems);
      }
    },
    restoreRow(row) {
      this.$emit('restore', row.id + (row.folderItem ? '/' : ''));
    },
    restoreSelected(rowArray) {
      const rowIds = rowArray.map(function (row) {
        return row.id + (row.folderItem ? '/' : '');
      });
      this.$emit('restore', rowIds);
    },
    restoreAll() {
      this.$emit('restore', '');
    },
    getMenuActions(row) {
      const actions = [];
      actions.push({
        type: 'highlight',
        label: this.$t('shared-link.links-table.action.restore'),
        command: 'restore'
      });
      return actions;
    },
    handleSelect(selectedItems) {
      this.selectedItems = selectedItems;
    },
    sidebarClose() {
      this.$refs.table.clearSelection();
      this.selectedItems = [];
    },
    handleSortChange(column) {
      this.sortLinkContentItems({
        items: this.getLinkContentItems,
        columnName: column.prop,
        sortOrder: column.order
      });
    }
  },
  mounted() {
    this.rootFolderName = '';
    this.loadContent({ path: '' });
  }

};
</script>
